import { useNavigationItems } from '@src/hooks/useNavigationItems';
import {
  Divider,
  DropdownHeader,
  DropdownItem,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import { getPinSection } from '../../utils/getPinSection';

/** Nav items that have child routes shown in the submenu sidebar (same as NavigationSidebar). */
function hasSubmenuItems(item: { children?: unknown[] }): boolean {
  return !!item.children?.length;
}

export function PinMenu() {
  const mixpanel = useMixpanel();
  const { navItems } = useNavigationItems();
  const { pinNavigationSidebarSection, setPinNavigationSidebarSection } = useDisplaySettings();
  const navItemsWithSubmenu = navItems.filter(hasSubmenuItems);
  const pinnedItems = navItemsWithSubmenu.filter(
    navItem => pinNavigationSidebarSection[getPinSection(navItem.path ?? '')]
  );

  const handleChangeAll = useCallback(
    (pinned: boolean) => {
      navItemsWithSubmenu.forEach(navItem => {
        const section = getPinSection(navItem.path ?? '');
        mixpanel.track(MixpanelEvent.ChangeSidebarPin, { [MixpanelEventProperty.Pinned]: pinned, section });
        setPinNavigationSidebarSection(pinned, section);
      });
    },
    [setPinNavigationSidebarSection, navItemsWithSubmenu, mixpanel]
  );

  const handlePinAllSections = useCallback(() => {
    handleChangeAll(true);
  }, [handleChangeAll]);

  const handleUnpinAllSections = useCallback(() => {
    handleChangeAll(false);
  }, [handleChangeAll]);

  const handlePinSection = useCallback(
    (path: string) => {
      const section = getPinSection(path);
      mixpanel.track(MixpanelEvent.ChangeSidebarPin, {
        [MixpanelEventProperty.Pinned]: !pinNavigationSidebarSection[section],
        section,
      });
      setPinNavigationSidebarSection(!pinNavigationSidebarSection[section], section);
    },
    [setPinNavigationSidebarSection, pinNavigationSidebarSection, mixpanel]
  );

  return (
    <>
      <DropdownHeader textTransform="uppercase">
        {pinnedItems.length} of {navItemsWithSubmenu.length} sections pinned
      </DropdownHeader>
      {navItemsWithSubmenu.map(navItem => {
        const isPinned = pinNavigationSidebarSection[getPinSection(navItem.path ?? '')];
        return (
          <DropdownItem
            data-testid={`pin-menu-item-${navItem.path}`}
            key={navItem.path}
            onClick={() => handlePinSection(navItem.path ?? '')}
            startIcon={navItem.icon}
            suffix={
              <Icon
                icon={isPinned ? IconName.Pin : IconName.PinOff}
                color={isPinned ? 'colorTextAttention' : 'colorTextSubtle'}
              />
            }
          >
            {navItem.label}
          </DropdownItem>
        );
      })}
      <Divider />
      <DropdownItem
        data-testid="pin-menu-pin-all"
        onClick={handlePinAllSections}
        disabled={pinnedItems.length === navItemsWithSubmenu.length}
        startIcon={IconName.Pin}
      >
        Pin All Sections
      </DropdownItem>
      <DropdownItem
        data-testid="pin-menu-unpin-all"
        onClick={handleUnpinAllSections}
        disabled={pinnedItems.length === 0}
        startIcon={IconName.Pin}
      >
        Unpin All Sections
      </DropdownItem>
    </>
  );
}
